import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  TextField,
  Button,
  Grid,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Avatar,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { getSecureLocalItem } from "utils/secureSessionStorage";
import { showSuccessToast } from "utils/toastNotification";

const Settings = () => {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  // Retrieve user & subscription information
  const userRaw = getSecureLocalItem("user");
  const user = userRaw ? JSON.parse(userRaw) : null;
  const role = getSecureLocalItem("role") || "Company";
  const { subscription } = useSelector(
    (state) => state.subscription || { subscription: null }
  );

  // Local state for settings form fields
  const [companySettings, setCompanySettings] = useState({
    companyName: user?.company_name || "EBS Office Solutions",
    contactEmail: user?.email || "admin@ebsoffice.com",
    contactPhone: "+91 98765 43210",
    timezone: "UTC+05:30 (Kolkata)",
    language: "en",
    currency: "INR",
  });

  const [notificationSettings, setNotificationSettings] = useState({
    emailAlerts: true,
    leaveRequests: true,
    payrollReports: false,
    taskUpdates: true,
  });

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleCompanyChange = (e) => {
    const { name, value } = e.target;
    setCompanySettings((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNotificationChange = (e) => {
    const { name, checked } = e.target;
    setNotificationSettings((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleSave = () => {
    showSuccessToast("Settings updated successfully!");
  };

  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: { xs: 1, md: 3 } }}>
      {/* Settings Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: isDarkMode ? "#ffffff" : "#1f2937",
            mb: 1,
            letterSpacing: "-0.5px",
          }}
        >
          System Settings
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: isDarkMode ? "#9ca3af" : "#6b7280" }}
        >
          Manage your organization settings, notifications, and application preferences.
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Side: Navigation Tabs */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: "20px",
              boxShadow: `0 10px 30px ${alpha(
                theme.palette.primary.main,
                isDarkMode ? 0.05 : 0.02
              )}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 3, textAlign: "center", bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <Avatar
                  sx={{
                    width: 72,
                    height: 72,
                    mx: "auto",
                    mb: 2,
                    bgcolor: "primary.main",
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.3)}`,
                  }}
                >
                  <BusinessIcon sx={{ fontSize: 36, color: "#ffffff" }} />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {companySettings.companyName}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {role} • Plan: {subscription?.plan_name || "Trialing Plan"}
                </Typography>
              </Box>

              <Tabs
                orientation="vertical"
                value={activeTab}
                onChange={handleTabChange}
                aria-label="Settings configuration tabs"
                sx={{
                  borderRight: 0,
                  "& .MuiTabs-indicator": {
                    left: 0,
                    right: "auto",
                    width: "4px",
                    borderRadius: "0 4px 4px 0",
                  },
                  "& .MuiTab-root": {
                    alignItems: "flex-start",
                    textAlign: "left",
                    pl: 3,
                    py: 2,
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    transition: "all 0.2s",
                    color: isDarkMode ? "#9ca3af" : "#4b5563",
                    "&.Mui-selected": {
                      color: "primary.main",
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    },
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.02),
                    },
                  },
                }}
              >
                <Tab
                  icon={<BusinessIcon sx={{ fontSize: 20, mr: 1.5 }} />}
                  iconPosition="start"
                  label="Company Profile"
                />
                <Tab
                  icon={<NotificationsIcon sx={{ fontSize: 20, mr: 1.5 }} />}
                  iconPosition="start"
                  label="Notifications"
                />
                <Tab
                  icon={<PaletteIcon sx={{ fontSize: 20, mr: 1.5 }} />}
                  iconPosition="start"
                  label="Preferences"
                />
              </Tabs>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side: Tab Panels */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: "20px",
              boxShadow: `0 10px 30px ${alpha(
                theme.palette.primary.main,
                isDarkMode ? 0.05 : 0.02
              )}`,
              border: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
              p: { xs: 2, md: 3 },
            }}
          >
            {/* TAB 1: Company Profile */}
            {activeTab === 0 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Organization Details
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Company Name"
                      name="companyName"
                      value={companySettings.companyName}
                      onChange={handleCompanyChange}
                      disabled={role !== "Company"}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      name="contactEmail"
                      value={companySettings.contactEmail}
                      onChange={handleCompanyChange}
                      disabled={role !== "Company"}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      name="contactPhone"
                      value={companySettings.contactPhone}
                      onChange={handleCompanyChange}
                      disabled={role !== "Company"}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Subdomain / Tenant Key"
                      value={user?.subdomain || "ebs-tenant"}
                      disabled
                      variant="outlined"
                      helperText="Tenant subdomains cannot be changed once initialized."
                    />
                  </Grid>
                </Grid>

                <Divider sx={{ my: 4 }} />

                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  Active Subscription Details
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Plan Name
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {subscription?.plan_name || "Professional Yearly"}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Max Employees / Users
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {subscription?.max_employees || 200} Users
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="textSecondary">
                      Expiration Date
                    </Typography>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {subscription?.current_period_end
                        ? new Date(subscription.current_period_end).toLocaleDateString()
                        : "N/A"}
                    </Typography>
                  </Grid>
                </Grid>

                {role === "Company" && (
                  <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveIcon />}
                      onClick={handleSave}
                      sx={{
                        borderRadius: "10px",
                        px: 3,
                        py: 1,
                        boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                      }}
                    >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Box>
            )}

            {/* TAB 2: Notifications */}
            {activeTab === 1 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Alert & Notification Preferences
                </Typography>
                <Stack spacing={3}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.emailAlerts}
                        onChange={handleNotificationChange}
                        name="emailAlerts"
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Global Email Alerts
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Receive system digests and updates via email.
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.leaveRequests}
                        onChange={handleNotificationChange}
                        name="leaveRequests"
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Leave Request Notifications
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Get notified when employees apply, cancel, or modify leaves.
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.payrollReports}
                        onChange={handleNotificationChange}
                        name="payrollReports"
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Payroll Distribution Reminders
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Alert me when payroll reports are ready to review and disburse.
                        </Typography>
                      </Box>
                    }
                  />
                  <Divider />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={notificationSettings.taskUpdates}
                        onChange={handleNotificationChange}
                        name="taskUpdates"
                        color="primary"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          Task & Project Milestones
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Receive notifications when task statuses transition or files are shared.
                        </Typography>
                      </Box>
                    }
                  />
                </Stack>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                      borderRadius: "10px",
                      px: 3,
                      py: 1,
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                    }}
                  >
                    Save Notifications
                  </Button>
                </Box>
              </Box>
            )}

            {/* TAB 3: Preferences */}
            {activeTab === 2 && (
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                  Regional & App Settings
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="select-language-label">Language</InputLabel>
                      <Select
                        labelId="select-language-label"
                        name="language"
                        value={companySettings.language}
                        onChange={handleCompanyChange}
                        label="Language"
                      >
                        <MenuItem value="en">English (US)</MenuItem>
                        <MenuItem value="es">Español</MenuItem>
                        <MenuItem value="fr">Français</MenuItem>
                        <MenuItem value="de">Deutsch</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="select-timezone-label">Timezone</InputLabel>
                      <Select
                        labelId="select-timezone-label"
                        name="timezone"
                        value={companySettings.timezone}
                        onChange={handleCompanyChange}
                        label="Timezone"
                      >
                        <MenuItem value="UTC-05:00 (EST)">UTC-05:00 (New York)</MenuItem>
                        <MenuItem value="UTC+00:00 (GMT)">UTC+00:00 (London)</MenuItem>
                        <MenuItem value="UTC+01:00 (CET)">UTC+01:00 (Paris)</MenuItem>
                        <MenuItem value="UTC+05:30 (Kolkata)">UTC+05:30 (New Delhi)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel id="select-currency-label">Default Currency</InputLabel>
                      <Select
                        labelId="select-currency-label"
                        name="currency"
                        value={companySettings.currency}
                        onChange={handleCompanyChange}
                        label="Default Currency"
                      >
                        <MenuItem value="USD">USD ($)</MenuItem>
                        <MenuItem value="EUR">EUR (€)</MenuItem>
                        <MenuItem value="INR">INR (₹)</MenuItem>
                        <MenuItem value="GBP">GBP (£)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    sx={{
                      borderRadius: "10px",
                      px: 3,
                      py: 1,
                      boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                    }}
                  >
                    Save Preferences
                  </Button>
                </Box>
              </Box>
            )}
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
